import { useEffect, useState } from "react";
import { MdDelete, MdEdit } from "react-icons/md";
import AddContractCategoryModal from "./component/AddContractCategoryModal";
import GlobalPagination from "../../../Components/Pagination/GlobalPagination";
import { useContractCategory } from "../useHook";
import Swal from "sweetalert2";
import Loader, { ImageWithFallback } from "../../../Utils/loader";
import {
  FiEdit2,
  FiFilter,
  FiPlus,
  FiRotateCcw,
  FiSearch,
} from "react-icons/fi";
import {
  usePermission,
  SystemPermissions,
} from "../../../Utils/permissionAccess";

function ContractCategories() {
  const { hasPermission } = usePermission();
  const canCreate = hasPermission(SystemPermissions.ContractCategories.CREATE);
  const canUpdate = hasPermission(SystemPermissions.ContractCategories.UPDATE);
  const canDelete = hasPermission(SystemPermissions.ContractCategories.DELETE);
  const canShowActions = canUpdate || canDelete;

  const [modalOpen, setModalOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const [offset, setOffset] = useState(0);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");

  const {
    addContractCategory,
    editContractCategory,
    getContractCategories,
    deleteContractCategory,
  } = useContractCategory();

  const closeModal = () => {
    setModalOpen(false);
    setCurrentCategory(null);
  };

  const fetchCategories = async (currentOffset = offset, showLoader = true) => {
    if (showLoader) {
      setIsLoading(true);
    }
    try {
      await getContractCategories(
        setCategories,
        setTotal,
        currentOffset,
        limit,
        searchQuery
      );
    } catch (error) {
      console.error("Error fetching contract categories:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = async () => {
    setOffset(0);
    fetchCategories(0);
  };

  const handleReset = () => {
    setSearchQuery("");
    setOffset(0);
    fetchCategories(0);
  };

  useEffect(() => {
    fetchCategories();
  }, [offset]);

  const handlePageChange = (newOffset) => {
    setIsLoading(true);
    setOffset(newOffset);
    fetchCategories(newOffset, false);
  };

  const handleSaveCategory = async (categoryData, id = null) => {
    setIsLoading(true);
    try {
      let response;
      if (id) {
        response = await editContractCategory(id, categoryData);
      } else {
        response = await addContractCategory(categoryData);
        setOffset(0);
      }

      if (response) {
        await fetchCategories(id ? offset : 0);
        setModalOpen(false);
        setCurrentCategory(null);
        Swal.fire("Success", "Contract category saved successfully!", "success");
      }
    } catch (error) {
      console.error("Error in handleSaveCategory:", error);
      Swal.fire("Error", error.message || "Failed to save category.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditCategory = (category) => {
    setCurrentCategory(category);
    setModalOpen(true);
  };

  const handleDeleteCategory = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });
    if (result.isConfirmed) {
      setIsLoading(true);
      try {
        const response = await deleteContractCategory(id);
        if (response) {
          await fetchCategories(offset);
          Swal.fire("Success", "Contract category deleted successfully!", "success");
        }
      } catch (error) {
        console.error("Error in handleDeleteCategory:", error);
        Swal.fire("Error", error.message || "Failed to delete category.", "error");
      } finally {
        setIsLoading(false);
      }
    }
  };

  if (isLoading && categories.length === 0) {
    return <Loader message="Loading contract categories..." />;
  }

  return (
    <div>
      <div className="flex flex-wrap gap-4 justify-between items-center">
        <h2 className="text-2xl font-bold">Contract Categories</h2>

        {canCreate && (
          <button
            onClick={() => {
              setCurrentCategory(null);
              setModalOpen(true);
            }}
            className="btn btn-primary h-[44px]"
          >
            <FiPlus className="text-white" />
            Add Category
          </button>
        )}
      </div>

      <div className="bg-white p-5 w-full rounded-lg pagination-shadow flex flex-wrap xlCustom:flex-nowrap xlCustom:justify-end items-center gap-3 my-5">
        <div className="search-wrapper">
          <input
            type="text"
            className="search-input"
            placeholder="Search by title or description"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <FiSearch className="search-icon" />
        </div>

        <div className="flex flex-wrap md:flex-nowrap gap-2">
          <button className="btn btn-filter filter-btn" onClick={handleSearch}>
            <FiFilter className="text-white" /> Apply Filter
          </button>
          <button
            className="btn btn-reset-filter filter-btn"
            onClick={handleReset}
          >
            <FiRotateCcw className="text-white" /> Reset Filter
          </button>
        </div>
      </div>

      <div className="bg-white w-full rounded-lg pagination-shadow">
        <div className="overflow-x-auto">
          <table className="min-w-full table-auto bg-white rounded-lg overflow-hidden">
            <thead>
              <tr className="brand-gradient text-white">
                <th className="table-th">#</th>
                <th className="table-th">Image</th>
                <th className="table-th">Title</th>
                <th className="table-th text-center">Contract Count</th>
                <th className="table-th">Custom URL</th>
                {canShowActions && <th className="table-th">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {categories.length === 0 ? (
                <tr>
                  <td
                    colSpan={canShowActions ? 6 : 5}
                    className="text-center py-10 text-gray-500 text-lg font-medium"
                  >
                    No contract categories found
                  </td>
                </tr>
              ) : (
                categories.map((category, index) => (
                  <tr
                    key={category.id}
                    className={`${
                      index % 2 === 0 ? "bg-gray-50" : "bg-gray-100"
                    } hover:bg-gray-200 transition-colors duration-300`}
                  >
                    <td className="table-td">{offset + index + 1}</td>
                    <td className="table-td">
                      <ImageWithFallback
                        src={category.image}
                        alt={category.title}
                        className="w-16 h-16 object-cover rounded-lg shadow-md"
                      />
                    </td>
                    <td className="table-td font-medium">{category.title}</td>
                    <td className="table-td text-center font-bold text-[#792099]">
                      {category.contractCount || 0}
                    </td>
                    <td className="table-td text-blue-600">{category.custom_url}</td>
                    {canShowActions && (
                      <td className="table-td">
                        <div className="flex space-x-2">
                          {canUpdate && (
                            <button
                              className="btn btn-edit text-white px-3"
                              onClick={() => handleEditCategory(category)}
                            >
                              <FiEdit2 className="text-white" /> Edit
                            </button>
                          )}
                          {canDelete && (
                            <button
                              className="btn btn-delete text-white px-3"
                              onClick={() => handleDeleteCategory(category.id)}
                            >
                              <MdDelete className="text-white" /> Delete
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {total > limit && (
        <div className="mt-6">
          <GlobalPagination
            offset={offset}
            limit={limit}
            total={total}
            onPageChange={handlePageChange}
            className="bg-white"
          />
        </div>
      )}

      <AddContractCategoryModal
        modalOpen={modalOpen}
        toggleModal={closeModal}
        category={currentCategory}
        handleSaveCategory={handleSaveCategory}
      />
    </div>
  );
}

export default ContractCategories;
